// Zentrales Display-Modell für eine einzelne Transaktion.
// Konsolidiert die Per-Typ-Verzweigungen aus WalletRow (WalletScreen.js),
// ActivityRow (AssetDetailScreen.js) und rowFromTx (WalletDesktopTable.js).
//
// Berechnungen (FIFO, Lots, mergeTxsByDay, transferMatcher, canonicalNormalizer)
// werden NICHT angefasst — buildTxView ist rein Display-seitig.

import { getTxLabel, getTxMeta, getTxIconPath, INCOME_TYPES, txDisplayCategory } from './transactionTypes';
import { resolveSwapEurValue } from './stablecoinResolver';
import { fmt, fmtC } from './format';

const FIAT_SET = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);

// Kurz-Label für die farbige Income-Badge (Staking/Airdrop/…). Macht den Zufluss-
// Typ auf einen Blick erkennbar — die ausführliche Bezeichnung (getTxLabel) ist
// als Headline zu lang. DACH-Produkt → deutsche Kurzform.
const INCOME_BADGE_LABEL = {
  staking: 'STAKING', airdrop: 'AIRDROP', mining: 'MINING', cashback: 'CASHBACK',
  interest: 'ZINSEN', lending: 'LENDING', farming: 'FARMING', mint: 'MINT',
};

const COIN_FULLNAME = {
  BTC: 'Bitcoin', ETH: 'Ethereum', SOL: 'Solana', XRP: 'XRP', ADA: 'Cardano',
  DOT: 'Polkadot', LINK: 'Chainlink', AVAX: 'Avalanche', MATIC: 'Polygon',
  HBAR: 'Hedera', BNB: 'BNB', DOGE: 'Dogecoin', LTC: 'Litecoin', SHIB: 'Shiba Inu',
  USDT: 'USDT', USDC: 'USDC', TRX: 'TRON', TON: 'Toncoin', SUI: 'Sui', NEAR: 'NEAR',
  UNI: 'Uniswap', ATOM: 'Cosmos', VET: 'VeChain', XLM: 'Stellar', ALGO: 'Algorand',
};
export function coinFullName(sym) {
  return COIN_FULLNAME[String(sym || '').toUpperCase()] || sym;
}

// Echter Krypto-Wallet-Transfer (externer Versand/Empfang oder gematchtes Paar)
// → aufklappbare Transfer-Karte (TransferChainCard Einzel-Bein-Modus). Bindet an
// die EINE Einordnungs-Wahrheit `txDisplayCategory`: NUR Kategorie 'transfer'.
// Damit bleiben gegenpartlose Zuflüsse (Airdrop/Reward → 'zufluss'), Dust und
// Fiat-Ein/Auszahlung ('fiat') außen vor — sie behalten ihre eigene Einordnung
// und Plakette (sonst „verschluckt" die Transfer-Karte sie, vgl. Regression).
export function isCryptoWalletTransfer(tx, transferMap) {
  // Alle drei Wallet-Bewegungs-Kategorien (gematchtes Paar + Einzel-Bein
  // gesendet/empfangen) bekommen die aufklappbare Transfer-Karte. Zuflüsse
  // (Airdrop/Reward → 'zufluss'), Dust und Fiat bleiben außen vor.
  const c = txDisplayCategory(tx, transferMap);
  return c === 'transfer' || c === 'gesendet' || c === 'empfangen';
}

// ── Scam-Dust-Entscheidung (Address-Poisoning), wert-basiert ─────────────────
// `tx._scamDust` ist der billige Read-Layer-KANDIDAT (canonicalNormalizer:
// einzahlung + winzige Menge + kein Fiat-Gegenwert). Das eigentliche Kriterium
// ist aber der EUR-WERT, nicht die Menge: 0.005 BTC (~300 EUR) ist KEIN Dust,
// 0.000013 ALGO (~0 EUR) schon. Darum hier ein Wert-Veto, damit echte
// Klein-Eingaenge teurer Coins (BTC/ETH) NICHT faelschlich versteckt werden.
//   valueEur > 0  -> Dust nur wenn < 1 Cent
//   kein Wert     -> Fallback: nur mikroskopische Mengen (< 0.001), BTC-sicher
const SCAM_DUST_MAX_EUR    = 0.01;
const SCAM_DUST_MICRO_MENGE = 0.001;
export function resolveScamDust(tx, valueEur) {
  if (!tx || !tx._scamDust) return false;
  const v = Number(valueEur);
  if (Number.isFinite(v) && v > 0) return v < SCAM_DUST_MAX_EUR;
  return Number(tx.menge) < SCAM_DUST_MICRO_MENGE;
}

// Aktueller EUR-Wert einer TX aus marketData (Menge x aktueller Preis), 0 wenn
// kein Preis bekannt. Zentral, damit Filter (WalletScreen) und Badge (buildTxView)
// dieselbe Quelle nutzen.
export function txValueFromMarket(tx, marketData) {
  const price = Number(marketData?.[tx?.coinId]?.price);
  const menge = Number(tx?.menge);
  return (Number.isFinite(price) && price > 0 && Number.isFinite(menge)) ? menge * price : 0;
}

// Set der TX-IDs, die als Scam-Dust gelten: Read-Layer-Kandidat (`_scamDust`)
// PLUS Wert-Veto über den aktuellen Marktpreis. EINE Quelle für alle Stellen,
// die Dust ausblenden — WalletScreen, AssetDetail-Activity UND die Haltefrist-
// Lots (taxLots) — damit der Filter überall identisch greift.
// Staking-Rewards (z.B. HBAR `-staking-reward`) sind typ:'staking', bekommen nie
// `_scamDust` (das setzt der Read-Layer nur bei typ:'einzahlung') und bleiben so
// garantiert erhalten.
// `derivedValues` (optional): die historisch abgeleiteten EUR-Werte, die die
// Zeile ohnehin anzeigt (useHistoricalPrices). Wichtig, weil obskure Scam-Coins
// oft KEINEN aktuellen Marktpreis haben (txValueFromMarket=0), aber sehr wohl
// einen Tageswert — ohne diesen fällt das Veto auf eine zu strenge Mengen-
// Schwelle zurück und lässt z.B. 0,001 ONDO (~0,0004 €) durch. Mit dem Tageswert
// entscheidet der echte EUR-Wert: teure Mini-Beträge (0,005 BTC ~300 €) bleiben
// sichtbar, preislose Mikro-Eingänge ohne Gegenwert sind Dust.
export function scamDustTxIds(txs, marketData, derivedValues) {
  const ids = new Set();
  for (const tx of txs || []) {
    if (!tx?._scamDust) continue;
    const id = tx._docId || String(tx.id);
    const v = txValueFromMarket(tx, marketData) || Number(derivedValues?.get?.(id)) || 0;
    if (resolveScamDust(tx, v)) ids.add(id);
  }
  return ids;
}

// Liefert `txs` ohne die Scam-Dust-Einträge (oder das Original, wenn keiner da
// ist — referenz-stabil, damit downstream-Caches wie der FIFO-Pool in taxLots
// nicht unnötig invalidiert werden).
export function dropScamDustTxs(txs, marketData, derivedValues) {
  const ids = scamDustTxIds(txs, marketData, derivedValues);
  if (!ids.size) return txs;
  return txs.filter(tx => !ids.has(tx._docId || String(tx.id)));
}

// Liefert ein flaches View-Objekt für eine TX. Theme-Tokens werden später
// in der Komponente aufgelöst (T.green / T.red), damit dieses Modell
// theme-agnostisch bleibt.
export function buildTxView(tx, ctx = {}) {
  const { transferMap, derivedValues, marketData, locale = 'de-DE' } = ctx;
  const id = tx._docId || String(tx.id);
  const meta = getTxMeta(tx.typ);
  const typLabel = (getTxLabel(tx.typ) || 'TX').toUpperCase();

  const isSwap = tx.typ === 'swap';
  const isTransferIn = tx.typ === 'einzahlung';
  const isTransferOut = tx.typ === 'auszahlung';
  const isTransfer = isTransferIn || isTransferOut;
  const isBuy = tx.typ === 'kauf';
  const isSell = tx.typ === 'verkauf';
  const isIncome = INCOME_TYPES.has(tx.typ);

  // ── Swap ──────────────────────────────────────────────────────────
  if (isSwap) {
    const coinInUp = String(tx.coinIn || tx.symIn || '').toUpperCase();
    const isFiatQuote = FIAT_SET.has(coinInUp);
    // Anzeige-Wert (REIN DISPLAY, keine Kostenbasis): Fiat-Quote > echter preis >
    // Stablecoin-Peg (exakt) > historischer Marktwert der bekannten Seite
    // (derivedValues, via useHistoricalPrices — füllt z.B. „SOL → NAKMAK" 0 €).
    const swapStable = resolveSwapEurValue(tx.coinOut, tx.mengeOut, tx.coinIn, tx.mengeIn)?.value;
    const dv = Number(derivedValues?.get(id));
    const displayValueEur = isFiatQuote && Number(tx.mengeIn) > 0
      ? Number(tx.mengeIn)
      : Number(tx.preis) > 0
        ? Number(tx.preis)
        : Number(swapStable) > 0
          ? Number(swapStable)
          : (dv > 0 ? dv : 0);

    return {
      id, raw: tx,
      kind: 'swap',
      typKey: tx.typ, typLabel, typColorKind: 'swap', typColorMeta: meta.color,
      primaryCoin: tx.coinOut,
      secondaryCoin: tx.coinIn,
      swapHeadline: `${tx.coinOut} → ${tx.coinIn}`,
      tradeHeadline: null,
      datum: tx.datum,
      exchangeName: tx.exchange || '—',
      walletName: null,
      mengeStr: null,
      mengeLabel: `${fmtC(tx.mengeOut)} ${tx.coinOut} → ${fmtC(tx.mengeIn)} ${tx.coinIn}`,
      flow: null,
      displayValueEur,
      valueIsDerived: false,
      valueKind: null,
      isSwap, isTransfer: false, isTransferIn: false,
      isBuy: false, isSell: false, isIncome: false,
      isUnmatched: false, isExternalTransfer: false,
      isScamDust: false,
    };
  }

  // ── Transfer ──────────────────────────────────────────────────────
  if (isTransfer) {
    const info = transferMap?.get(id);
    // Ein bewiesener eigener Wallet-Transfer (Adress-Treffer) ist NIE ein „offener"
    // Transfer — der Gegenpart ist bekannt (die eigene Wallet), auch ohne erfasste
    // Gegenbuchung.
    const unmatched = info?.matchStatus === 'unmatched' && !tx.externalTransfer && !tx.ownWalletTransfer;
    const partner = info?.matchedPlatform;
    // Unbekannter Gegenpart: nie mehr „?" anzeigen. Wenn der Matcher keine
    // Plattform liefert, klassifizieren wir nach Coin-Art — Fiat (EUR/USD…) ist
    // eine Bank-Ein/Auszahlung („Bank"), Krypto ein Wallet-Transfer („Externe
    // Wallet"). So bleibt die Quelle-Cell immer aussagekräftig.
    const isFiatCoin = FIAT_SET.has(String(tx.coin || '').toUpperCase());
    const unknownPartyLabel = isFiatCoin ? 'Bank' : 'Externe Wallet';
    const externalLabel = tx.externalTransfer ? 'Externe Wallet' : null;
    // Adress-Treffer → konkreter Wallet-Name („Ledger") statt „Externe Wallet".
    const ownLabel = (tx.ownWalletTransfer && tx.ownWalletLabel) ? tx.ownWalletLabel : null;
    const isIn = isTransferIn;
    const fromName = isIn ? (partner || ownLabel || externalLabel || unknownPartyLabel) : (tx.exchange || '—');
    const toName = isIn ? (tx.exchange || '—') : (partner || ownLabel || externalLabel || unknownPartyLabel);
    const derived = derivedValues?.get(id);
    const hasRealPreis = Number(tx.preis) > 0;
    // Fiat-Transfer (Bank-Ein/Auszahlung in EUR/USD…): die MENGE ist bereits der
    // EUR-Wert (100 EUR = 100,00 €) — exakt, kein Preis-Lookup/Schätzung nötig.
    // Krypto-Transfer ohne erfassten Preis → abgeleiteter Tagespreis (~).
    const displayValueEur = hasRealPreis
      ? Number(tx.preis)
      : isFiatCoin
        ? (Number(tx.menge) || 0)
        : (Number(derived) || 0);
    const valueIsDerived = !hasRealPreis && !isFiatCoin && Number(derived) > 0;
    // External-Icon: outgoing → immer wenn extern; incoming → nur wenn ohne Partner
    const showToExtIcon = !!tx.externalTransfer && ((isIn && !partner) || !isIn);
    const fromMuted = !isIn && !!tx.externalTransfer;
    const toMuted = showToExtIcon;

    return {
      id, raw: tx,
      kind: 'transfer',
      typKey: tx.typ, typLabel: isIn ? '↓ EINGANG' : '↑ AUSGANG',
      typColorKind: 'transfer', typColorMeta: '#60A5FA',
      primaryCoin: tx.coin,
      secondaryCoin: null,
      swapHeadline: null,
      tradeHeadline: null,
      datum: tx.datum,
      exchangeName: tx.exchange || '—',
      walletName: null,
      mengeStr: `${fmt(tx.menge, locale)} ${tx.coin}`,
      mengeLabel: `${fmtC(tx.menge)} ${tx.coin}`,
      flow: {
        fromName, toName,
        fromMuted, toMuted,
        showToExtIcon,
        arrowColor: '#60A5FA',
        font: 'display',
        isTransferFlow: true,
      },
      displayValueEur,
      valueIsDerived,
      valueKind: null,
      isSwap: false, isTransfer: true, isTransferIn: isIn,
      isFiatTransfer: isFiatCoin,
      // 'transfer' = echter Wallet-Move (gold „transferiert"); 'zufluss' =
      // gegenpartloser Eingang (Airdrop/Reward → „erhalten"); 'fiat' = Bank.
      displayCategory: txDisplayCategory(tx, transferMap),
      isBuy: false, isSell: false, isIncome: false,
      isUnmatched: !!unmatched,
      isExternalTransfer: !!tx.externalTransfer,
      isScamDust: isIn && resolveScamDust(tx, txValueFromMarket(tx, marketData)),
    };
  }

  // ── Alle übrigen Typen (kauf, verkauf, staking, airdrop, mining, …) ──
  const headline = isBuy
    ? `${coinFullName(tx.coin)} gekauft`
    : isSell
      ? `${coinFullName(tx.coin)} verkauft`
      : isIncome
        // Income: Coin-Name als Headline, der Typ (Staking/Airdrop/…) wandert in
        // die farbige Badge daneben — so ist der Zufluss-Typ sofort erkennbar.
        ? coinFullName(tx.coin)
        : `${coinFullName(tx.coin)} · ${getTxLabel(tx.typ)}`;

  // Farbige Typ-Badge nur für Einkommen (Staking/Airdrop/Mining/…): macht den
  // Zufluss-Typ als eigenständiges Signal sichtbar (Akzent nur Schrift/Stroke,
  // kein gefüllter Container). Andere Typen: keine Badge (null).
  const typBadge = isIncome
    ? { label: INCOME_BADGE_LABEL[tx.typ] || getTxLabel(tx.typ).split(' ')[0].toUpperCase(), color: meta.color, iconPath: getTxIconPath(tx.typ) }
    : null;

  // Wallet-Kette (z.B. MoonPay → Ledger) ist eine Flow, die NICHT im Headline-Slot,
  // sondern im Sub-Slot / Quelle-Cell gerendert wird.
  const walletChain = tx.wallet
    ? {
        fromName: tx.exchange || '—',
        toName: `${tx.wallet} Wallet`,
        fromMuted: false,
        toMuted: false,
        showToExtIcon: false,
        arrowColor: null, // sub-grau via T.sub in der Komponente
        font: 'family',
        isTransferFlow: false,
      }
    : null;

  const typColorKind = isBuy ? 'buy' : isSell ? 'sell' : 'meta';

  // Anzeige-Wert für eigenständige Gebühren (typ='gebuehr'): statt 0,00 € den
  // realen EUR-Gegenwert zeigen — Fiat-Gebühr = Betrag selbst, Coin-Gebühr =
  // Menge×histor. Preis (aus derivedValues). REINE Anzeige: tx.preis/Bestand/
  // FIFO bleiben unangetastet (Kernberechnung unverändert).
  const isFee = tx.typ === 'gebuehr';
  const isFutures = tx.typ === 'futures_pnl' || tx.typ === 'funding_fee';
  const hasRealPreis = Number(tx.preis) > 0;
  let displayValueEur = hasRealPreis ? Number(tx.preis) : 0;
  let valueIsDerived = false;
  // valueKind: signiertes Gewinn/Verlust-Rendering (Vorzeichen + Farbe) für
  // Futures-PnL/Funding-Fee — analog zur mobilen FuturesPnlCard. null = neutral.
  let valueKind = null;
  if (isFutures) {
    // preis ist der signierte EUR-Wert (negativ = Verlust/Kosten). Die `> 0`-
    // Logik oben verschluckte Verluste zu „0,00 €" — hier signiert übernehmen.
    const signed = Number(tx.preis) || 0;
    displayValueEur = signed;
    valueKind = signed < 0 ? 'loss' : signed > 0 ? 'gain' : 'zero';
  } else if (isFee && !hasRealPreis) {
    if (FIAT_SET.has(String(tx.coin || '').toUpperCase())) {
      displayValueEur = Number(tx.menge) || 0; // Fiat-Gebühr: Betrag = EUR-Wert (exakt)
    } else {
      const derivedFee = Number(derivedValues?.get(id)) || 0;
      if (derivedFee > 0) { displayValueEur = derivedFee; valueIsDerived = true; }
    }
  } else if (isIncome && !hasRealPreis) {
    // Income (Staking-Reward, Airdrop, Mining, Interest, …): EUR-Wert ab Import
    // i.d.R. 0 → menge × histor. Tagespreis (aus derivedValues) ableiten. Auch
    // Minimalbeträge sind Zuflüsse und werden in EUR ausgewiesen.
    if (FIAT_SET.has(String(tx.coin || '').toUpperCase())) {
      displayValueEur = Number(tx.menge) || 0; // Fiat-Income: Betrag = EUR-Wert
    } else {
      const derivedInc = Number(derivedValues?.get(id)) || 0;
      if (derivedInc > 0) { displayValueEur = derivedInc; valueIsDerived = true; }
    }
  }

  return {
    id, raw: tx,
    kind: isIncome ? 'income' : (isBuy || isSell) ? 'trade' : (meta.category || 'other'),
    typKey: tx.typ, typLabel, typBadge,
    typColorKind, typColorMeta: meta.color,
    primaryCoin: tx.coin,
    secondaryCoin: null,
    swapHeadline: null,
    tradeHeadline: headline,
    datum: tx.datum,
    exchangeName: tx.exchange || '—',
    walletName: tx.wallet || null,
    mengeStr: null,
    mengeLabel: `${fmtC(tx.menge)} ${tx.coin}`,
    flow: walletChain,
    displayValueEur,
    valueIsDerived,
    valueKind,
    isSwap: false, isTransfer: false, isTransferIn: false,
    isBuy, isSell, isIncome,
    isUnmatched: false,
    isExternalTransfer: false,
    isScamDust: false,
  };
}

// ── Typ-Identität je Zeile: Name + Farbe + Zeichen (EINE Wahrheit) ───────────
// Quelle = das bestehende Typ-System (WIZARD_TYPES/TX_TYPES, Bild 1). Jede TX
// zeigt damit im Feed klar, WAS sie ist — farbcodiert, benannt, mit Zeichen.
// `glyph` = Schlüssel für das kleine SVG-Zeichen in TxRow (TYPE_GLYPHS).
// `dust:true` → Sonderfall „weiß mit schwarzem Pulver".
// Pastell-Palette (neue Listen-Optik): volle, ruhige Farben statt der gesättigten
// Signal-Töne. Vier Transfer-Fälle sind eigene Farben: Coin rein/raus (blau/orange)
// und Fiat rein/raus (cyan/rosé, Geldschein-Icon).
const TT_GREEN = '#5FB98A', TT_RED = '#DD7D72', TT_INDIGO = '#8E90D6',
  TT_BLUE = '#74A8DB', TT_ORANGE = '#E0976A', TT_PINK = '#DB8DB6',
  TT_FUNDING = '#DDA15C', TT_LOSS = '#C57371', TT_FIAT_IN = '#4FAEC0',
  TT_FIAT_OUT = '#D488A8',
  // Transfer (eigenes Paar) = eigenes Teal. Früher #D9AE57 — kollidierte exakt mit
  // Staking (#D9AE57) und lag nah an Mining/Interest. Teal hat einen eigenen
  // Farbraum (kein anderer Typ nutzt ihn) → klar als „interner Move" lesbar.
  TT_TEAL = '#2FA39A';

// `iconPath` = Linien-SVG (viewBox 0 0 24 24) fürs kartenhohe Typ-Wasserzeichen
// in TxRow. `glyph` bleibt für die alte TYPE_GLYPHS-Eckplakette (Tabellen-Variante).
const TAG_ICON = {
  down:     'M12 5v14M6 13l6 6 6-6',
  up:       'M12 19V5M6 11l6-6 6 6',
  swap:     'M4 9h13l-4-4M17 9l-4 4M20 15H7l4 4M7 15l4-4',
  plane:    'M22 3L11 14M22 3l-7 19-4-8-8-4z',
  transfer: 'M7 4 3 8l4 4M3 8h12M17 20l4-4-4-4M21 16H9',  // zwei gegenläufige Pfeile (eigenes Paar)
  banknote: 'M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zM12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM5.5 12h.01M18.5 12h.01',
  star:     'M12 3l2.6 6.3 6.8.5-5.2 4.4 1.7 6.6-5.7-3.6-5.7 3.6 1.7-6.6-5.2-4.4 6.8-.5z',
  // Percent = NUR Gebühr/Funding-Fee (Kosten). Einkommen bekommt eigene Icons.
  percent:  'M19 5L5 19M7.6 7.6a1.6 1.6 0 1 1 .01 0M16.4 16.4a1.6 1.6 0 1 1 .01 0',
  airdrop:  'M3 12a9 9 0 0 1 18 0M3 12l9 9.5 9-9.5M8.7 12L12 21.5M15.3 12L12 21.5',
  futures:  'M7 4v3M7 13v3M4.5 7h5v6h-5zM16.5 6v3M16.5 15v3M14 9h5v6h-5z',
  warning:  'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 12h8',
  trend:    'M22 7l-8.5 8.5-5-5L2 17M16 7h6v6',                 // Zinsen/Ertrag (steigend)
  sprout:   'M12 22V11M12 11C12 7.5 9.2 5.5 5 5.5c0 3.5 2.8 5.5 7 5.5zM12 11c0-3 2.4-5 6-5 0 3-2.4 5-6 5z', // Lending (wachsen lassen)
  dust:     'M12 4v4M12 16v4M4 12h4M16 12h4M6.5 6.5l2 2M15.5 15.5l2 2M17.5 6.5l-2 2M8.5 15.5l-2 2', // Staub/Funkel (Address-Poisoning)
};

const INCOME_TYPE_TAG = {
  staking:  { label: 'Staking',  color: '#D9AE57', glyph: 'star',    iconPath: TAG_ICON.star },
  airdrop:  { label: 'Airdrop',  color: '#A78FD9', glyph: 'airdrop', iconPath: TAG_ICON.airdrop },
  mining:   { label: 'Mining',   color: '#D9935A', glyph: 'star',    iconPath: TAG_ICON.star },
  cashback: { label: 'Cashback', color: '#5FB98A', glyph: 'star',    iconPath: TAG_ICON.star },
  interest: { label: 'Zinsen',   color: '#C9A24E', glyph: 'trend',   iconPath: TAG_ICON.trend },
  lending:  { label: 'Lending',  color: '#6FB8A0', glyph: 'sprout',  iconPath: TAG_ICON.sprout },
  farming:  { label: 'Farming',  color: '#86C98A', glyph: 'star',    iconPath: TAG_ICON.star },
  mint:     { label: 'Mint',     color: '#67C8D8', glyph: 'star',    iconPath: TAG_ICON.star },
};

// `matched` = echtes Wallet→Wallet-Paar (TransferChainCard, gold „Transfer");
// für TxRow-Singles ist es egal (Singles sind extern → Gesendet/Empfangen).
export function txTypeTag(view) {
  if (!view) return { label: '', color: '#888', glyph: 'dot', iconPath: null };
  if (view.isScamDust) return { label: 'Dust', color: '#9A9DA6', glyph: 'dust', dust: true, iconPath: TAG_ICON.dust };
  if (view.isSwap)     return { label: 'Tausch', color: TT_INDIGO, glyph: 'swap', iconPath: TAG_ICON.swap };
  if (view.isBuy)      return { label: 'Kauf', color: TT_GREEN, glyph: 'down', iconPath: TAG_ICON.down };
  if (view.isSell)     return { label: 'Verkauf', color: TT_RED, glyph: 'up', iconPath: TAG_ICON.up };
  if (view.typKey === 'futures_pnl') return { label: 'Futures PnL', color: TT_PINK, glyph: 'futures', iconPath: TAG_ICON.futures };
  if (view.typKey === 'funding_fee') return { label: 'Funding-Fee', color: TT_FUNDING, glyph: 'percent', iconPath: TAG_ICON.percent };
  if (view.typKey === 'gebuehr')     return { label: 'Gebühr', color: TT_FUNDING, glyph: 'percent', iconPath: TAG_ICON.percent };
  if (view.typKey === 'loss')        return { label: 'Verlust', color: TT_LOSS, glyph: 'warning', iconPath: TAG_ICON.warning };
  if (view.isIncome)   return INCOME_TYPE_TAG[view.typKey] || { label: getTxLabel(view.typKey), color: view.typColorMeta || '#888', glyph: 'star', iconPath: TAG_ICON.star };
  if (view.isTransfer) {
    // Fiat (Bank ↔ Börse): Geldschein-Icon, cyan rein / rosé raus.
    if (view.isFiatTransfer) {
      return view.isTransferIn
        ? { label: 'Einzahlung', color: TT_FIAT_IN, glyph: 'banknote', iconPath: TAG_ICON.banknote }
        : { label: 'Auszahlung', color: TT_FIAT_OUT, glyph: 'banknote', iconPath: TAG_ICON.banknote };
    }
    // Drei getrennte Kategorien — dieselbe Wahrheit wie die Tabs (txDisplayCategory):
    // echtes eigenes Paar = gold „Transfer"; Einzel-Bein extern = orange „Gesendet"
    // (Flieger) / blau „Empfangen" (Pfeil).
    if (view.displayCategory === 'gesendet')
      return { label: 'Gesendet', color: TT_ORANGE, glyph: 'plane', iconPath: TAG_ICON.plane };
    if (view.displayCategory === 'empfangen')
      return { label: 'Empfangen', color: TT_BLUE, glyph: 'down', iconPath: TAG_ICON.down };
    if (view.displayCategory === 'transfer')
      return { label: 'Transfer', color: TT_TEAL, glyph: 'transfer', iconPath: TAG_ICON.transfer };
    // Fallback (sollte nicht auftreten): nach Richtung.
    return view.isTransferIn
      ? { label: 'Empfangen', color: TT_BLUE, glyph: 'down', iconPath: TAG_ICON.down }
      : { label: 'Gesendet', color: TT_ORANGE, glyph: 'plane', iconPath: TAG_ICON.plane };
  }
  return { label: getTxLabel(view.typKey), color: view.typColorMeta || '#888', glyph: 'dot', iconPath: null };
}

// Gold für die gematchte Transfer-Paar-Karte (TransferChainCard) — eigenes
// „Transfer"-Tag, neutral im Betrag (gleicher Wert rein/raus).
export const TRANSFER_PAIR_TAG = { label: 'Transfer', color: TT_TEAL, glyph: 'transfer', iconPath: TAG_ICON.transfer };

// Hilfs-Resolver für die Komponente — gibt die finale Type-Color basierend
// auf Theme-Tokens zurück.
export function resolveTypColor(view, T) {
  // Calm-Doktrin: fast monochrom — nur Zugänge (Käufe) dezent grün, alles
  // andere neutral-warm. Kein Rot (Verkauf), kein Blau (Transfer/Swap).
  // AUSNAHME (bewusst, User-Wunsch): Einkommen (Staking/Airdrop/Mining/…) tragen
  // ihre Typ-Farbe — sie sollen klar als das erkennbar sein, was sie sind.
  if (view.isIncome) return view.typColorMeta || T.sub;
  switch (view.typColorKind) {
    case 'buy':      return T.green;
    default:         return T.sub;
  }
}
